import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { ButtonRounded } from '@comeon/ui';
import breakpoint, { getZindexByType, sizes } from '~/utils/style-utils';
import { WidgetData } from './utils';

const CardWrapper = styled.div`
	display: grid;

	max-width: 564px;

	border-radius: 12px;
`;

export const CardWrapperCountdown = styled(CardWrapper)`
	grid-template-columns: 104px calc(100vw - 232px) 104px;
	grid-template-rows: 104px;

	${breakpoint.lg`
			grid-template-columns: 162px 240px 162px;
			grid-template-rows: 162px;
	`}
`;

export const CardWrapperAvailable = styled(CardWrapper)`
	padding-right: 24px;
	grid-template-columns: auto minmax(50%, auto);
	gap: 30px;
	grid-template-rows: 104px;
	justify-items: center;
	width: 100%;

	${breakpoint.lg`
			grid-template-columns: 162px auto;
			grid-template-rows: 162px;
	`}
`;

export const BackGroundImage = styled.div<
	Pick<WidgetData, 'backgroundImage'> & {
		tmpGameIconSrc?: string;
	}
>`
	display: flex;
	justify-content: center;
	width: 100%;
	border-radius: 12px;

	background-color: rgba(var(--font-light), 1);
	background-image:
		linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
		url(${props => `${props.backgroundImage}?format=auto&width=${sizes.sm}`}), url(${props => props.tmpGameIconSrc});
	background-position: center;
	background-size: cover;

	cursor: pointer;

	${breakpoint.lg`
			margin: 0 12px;
			width: calc(100vw - 48px);
	`}

	${({ backgroundImage, tmpGameIconSrc }) => css`
		${breakpoint.md`background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage}?format=auto&width=${sizes.md}), url(${tmpGameIconSrc});`}
		${breakpoint.lg`background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage}?format=auto&width=${sizes.lg}), url(${tmpGameIconSrc});`}
		${breakpoint.xl`background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage}?format=auto&width=${sizes.xl}), url(${tmpGameIconSrc});`}
		${breakpoint.xxl`background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage}?format=auto&width=${sizes.xxl}), url(${tmpGameIconSrc});`}
	`}
`;

export const ForegroundImage = styled.div`
	position: relative;
	width: 128px;
	height: 128px;
	top: -12px;
	left: -12px;

	${breakpoint.lg`
		width: 162px;
		height: 162px;
		top: 0;
		left: 0;
	`}
`;

export const ForegroundImageContainer = styled.div`
	grid-area: image;
`;

const marquee = keyframes`
  100% {
    transform: translateX(-50%);
  }
`;

export const GameName = styled.p<{ showScrollTextComponent: boolean | undefined }>`
	color: rgba(var(--font-light), 1);
	font: var(--small);
	white-space: nowrap;
	width: fit-content;

	animation: ${marquee} 7s linear infinite;

	${({ showScrollTextComponent }) => (!showScrollTextComponent ? `animation: none;` : '')}

	${breakpoint.lg`
		font: var(--label-regular);
	`}
`;

export const GameNameWrapper = styled.div`
	max-width: 100%;
	overflow: hidden;
`;

export const CountDownWrapper = styled.div`
	display: flex;
	gap: 2px;

	grid-area: countdown;
`;

export const TimeWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	width: 40px;
	height: 44px;
	border-radius: 6px;
	padding: 6px, 8px, 4px, 8px;
	background-color: rgba(0, 0, 0, 0.5);
	color: rgba(var(--font-light), 1);

	${breakpoint.lg`
		width: 50px;
		height: 50px;	
	`}
`;

export const Time = styled.p`
	color: rgba(var(--font-light), 1);

	font: var(--label-bold);

	${breakpoint.lg`
		font: var(--h3);
	`}
`;

export const TimeAbbreviation = styled.small`
	color: rgba(var(--font-light), 1);
	text-transform: uppercase;

	font: var(--small-bold);

	@media (max-width: ${`${sizes.lg}px`}) {
		font: var(--label-bold);
		font-size: 8px;
		line-height: 12px;
	}
`;

export const PlayButton = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;

	& > svg > path {
		fill: rgba(var(--font-light), 1);
	}
`;

export const MiddleWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 6px;
	z-index: ${getZindexByType('base')};
	width: 100%;
`;

export const PlayGameButton = styled.button`
	height: 28px;
	padding: 0 12px;
	background-color: rgba(var(--font-light), 1);
	border-radius: 15px;
	color: rgba(var(--font-dark), 1);
	font: var(--small-bold);

	${breakpoint.lg`
		height: 36px;
		padding: 0 24px;
		border-radius: 100px;
		font: var(--label-bold);
	`}
`;

export const VerticalCardWrapper = styled.div<
	Pick<WidgetData, 'backgroundImage'> & {
		tmpGameIconSrc?: string;
	}
>`
	width: 340px;
	max-height: calc(100dvh - var(--regulationHeight) - 40px);
	border-radius: 12px;
	margin: auto;
	position: relative;

	display: grid;
	grid-template-rows: 44px minmax(auto, 24%) 180px 44px 44px 72px;
	gap: 12px;
	justify-items: center;
	align-items: center;

	grid-template-areas:
		'head'
		'image'
		'video'
		'comingsoon'
		'countdown'
		'button';

	background-color: rgba(var(--font-light), 1);
	background-image:
		linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
		url(${props => `${props.backgroundImage}?format=auto&height=640`}), url(${props => props.tmpGameIconSrc});
	background-position: center;
	background-size: cover;

	overflow: auto;
	transform: translate3d(0, 0, 0);
	-webkit-overflow-scrolling: touch;
	-webkit-transform: translateZ(0);

	${breakpoint.lg`
		grid-template-areas:
			'video image'
			'video comingsoon'
			'video countdown'
			'video button';

		grid-template-rows: 196px 36px 100px 108px;
		grid-template-columns: 480px 264px;
		width: auto;
		gap: 0;


		padding: 0 18px 0 18px;
	`}

	${({ backgroundImage, tmpGameIconSrc }) => css`
		${breakpoint.lg`background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage}?format=auto&width=446), url(${tmpGameIconSrc});`}
	`}
`;

export const VerticalCardTopWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px;
	padding-bottom: 0;
	width: 100%;

	grid-area: head;

	${breakpoint.lg`
		padding: 12px 18px 0 18px;
		position: absolute;
    top: 0;
    left: 0;
	`}
`;

export const LogoWrapper = styled.div`
	height: 20px;

	display: flex;
	align-items: center;
	gap: 8px;

	& > small {
		color: rgba(var(--font-light), 1);
		margin-top: 2px;
	}

	${breakpoint.lg`
		position: relative;
		left: -6px; 
	`}
`;

export const CloseButton = styled.button`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: rgba(249, 249, 249, 0.15);

	display: flex;
	align-items: center;
	justify-content: center;

	& > svg > path {
		fill: rgba(var(--font-light), 1);
	}

	${breakpoint.lg`
		position: relative;
		right: -6px; 
	`}
`;

export const TrailerWrapper = styled.div`
	border-radius: 12px;
	overflow: hidden;
	position: relative;

	grid-area: video;
`;

export const CenteredContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	width: 100%;

	grid-area: button;
`;

export const OtherExclusivesLink = styled.a`
	height: 36px;
	padding: 0 24px;
	background-color: transparent;
	color: rgba(var(--font-light), 1);
	font: var(--label-regular);
	text-decoration: underline;
	margin-top: 12px;

	margin: auto;
	display: flex;
	align-items: center;
`;

const ringAnimation = keyframes`
  0% { transform: rotateZ(0); }
  5% { transform: rotateZ(30deg); }
  15% { transform: rotateZ(-28deg); }
  25% { transform: rotateZ(34deg); }
  35% { transform: rotateZ(-32deg); }
  45% { transform: rotateZ(30deg); }
  55% { transform: rotateZ(-28deg); }
  65% { transform: rotateZ(26deg); }
  75% { transform: rotateZ(-24deg); }
  85% { transform: rotateZ(22deg); }
  95% { transform: rotateZ(0); }
  100% { transform: rotateZ(0); }
`;

export const SubscribeButton = styled(ButtonRounded)<{ isAnimating: boolean }>`
	height: 36px;
	padding: 0 16px;
	background-color: rgba(var(--font-light), 1);
	color: rgba(var(--font-dark), 1);
	font: var(--label-bold);

	& > svg {
		padding: 0;
		height: 24px;
		transform-origin: 50% 4px;
		transition: transform 0.3s ease;
		${props =>
			props.isAnimating &&
			css`
				animation: ${ringAnimation} 0.7s ease-in-out;
			`}
	}

	margin: 12px auto;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export const SubscribedText = styled.div`
	height: 36px;
	padding: 0 16px;
	background-color: transparent;
	color: rgba(var(--font-light), 1);
	font: var(--label-bold);

	& > svg {
		padding: 0;
		height: 24px;
	}

	margin: 12px auto;
	display: flex;
	align-items: center;
	gap: 8px;
`;
export const Backdrop = styled(motion.div)<{ hasStaticHeader: boolean }>`
	position: fixed;
	z-index: ${getZindexByType('exclusive-game-modal')};
	top: ${props => (props.hasStaticHeader ? 'var(--regulationHeight)' : '0px')};
	left: 0;
	height: calc(100% - ${props => (props.hasStaticHeader ? 'var(--regulationHeight)' : '0px')});
	width: 100%;
	background: rgba(0, 0, 0, 0.8);
	display: flex;
	justify-content: center;
	align-items: center;
`;

export const ComingSoonTitle = styled.h1`
	text-transform: var(--exclusive-games-text-transform, none);
	color: rgba(var(--font-light), 1);
	white-space: break-spaces;
	text-align: center;
	z-index: ${getZindexByType('base')};

	grid-area: comingsoon;

	font: var(--h1);
`;

export const GameTrailer = styled.video`
	height: 180px;
	width: 100%;
	object-fit: cover;
`;

export const ExclusiveCaption = styled.small`
	text-transform: uppercase;
	font: var(--caption-bold);
`;

export const TitleWrapper = styled.div`
	position: relative;
	width: 100%;
	height: 25px;

	${breakpoint.lg`
		height: 45px;
	`}
`;

export const ComingSoonCountdown = styled.h3`
	text-transform: var(--exclusive-games-secondary-text-transform, none);
	white-space: nowrap;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	color: rgba(var(--font-light), 1);

	font: var(--label-bold);

	${breakpoint.lg`
		font: var(--h3);
	`}
`;

export const AvailableNowTitle = styled.h2`
	text-transform: var(--exclusive-games-text-transform, none);
	white-space: nowrap;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	color: rgba(var(--font-light), 1);

	font: var(--h2);

	${breakpoint.lg`
		font: var(--display-3);
	`}
`;

export const MuteButton = styled.button`
	position: absolute;
	right: 6px;
	bottom: 6px;
`;
